import { useId, useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { Card, HStack } from '@components/base/layout';
import { Table } from '@components/base/data';
import { Text, Icon } from '@components/base/display';
import { Switch } from '@components/base/inputs';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
  safeString,
} from '@components/integrated/data-list';
import type { DataListConfig } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import type { CostLine } from '../types';
import { formatCurrencyFr } from '../configs/staticData';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CostSynthesisCardProps {
  /**
   * Every `WS2_GS_TAB_SINGAR` row, in API (`ORDRE`) order: per garantie, its
   * main row (`isTotal` — `CD_PSD === 'TOTAL'`) followed by its poste detail
   * rows (`isTotal === false`, label in `poste`). The card shows the main rows
   * only until the "Postes" toggle is switched on (mahhp 22854).
   */
  costs: CostLine[];
  /**
   * Authoritative cost total (header amount + `cost` footer figure). The page
   * sums the garanties' main rows; the card never re-derives it.
   */
  totalCost: number;
  /**
   * Tenant-tunable initial state of the "Postes" header toggle. `true` opens
   * the card with the poste detail rows expanded under each garantie;
   * `false` / `undefined` (the literal fallback) shows the garantie main rows
   * only — byte-identical baseline render. The handler can still flip the
   * toggle afterwards; until they do, the toggle follows this prop.
   * Tenant-overridable via `setProp`.
   */
  defaultShowPostes?: boolean;
  /**
   * Per-tenant column layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the column config is returned unchanged (byte-identical
   * render). Rule authors target the **stable column keys** below
   * (`guarantee` / `provision` / `settlement` / `grossCharge` / `forecast` /
   * `recourse` / `cost`) to rename / hide / reorder / tooltip a column
   * without code, and `title` to rename the card.
   */
  _layoutOverrides?: LayoutOverrides;
}

/** Stable registry / layout-config id of this card. */
const COMPONENT_ID = 'claimflow.costSynthesisCard';

/** Card-level texts (title / Postes toggle / Total row), kept out of the JSX. */
const COST_SYNTHESIS_CARD_TEXT = {
  title: 'Synthèse des coûts',
  postesToggle: 'Postes',
  totalRow: 'Total',
} as const;

// ---------------------------------------------------------------------------
// Shared cell formatting
//
// `formatCell` mirrors the original card: `null` → `'-'`, else
// `formatCurrencyFr`. Keeping a single shared formatter means the body cells
// AND the Total row render with the same formatting (and the unit-test mock of
// `formatCurrencyFr` still applies).
// ---------------------------------------------------------------------------

function formatCell(value: number | null): string {
  if (value === null) return '-';
  return formatCurrencyFr(value);
}

/** Coerces an unknown cell value to `number | null`. */
function toCostValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(num) ? null : num;
}

/**
 * A poste (detail) row — i.e. not a garantie's main `TOTAL` row. Only the
 * explicit `isTotal === false` flag qualifies, so a row without the flag
 * renders as a main row.
 */
function isPosteRow(row: Record<string, unknown>): boolean {
  return row.isTotal === false;
}

// ---------------------------------------------------------------------------
// Custom cell renderers
//
// The native `currency` cell renderer emits an empty string for `null`; the
// synthesis grid must show a dash, so the amount columns use a `custom`
// renderer: `formatCell` text in a `secondary` tone (the `cost` column adds
// `medium` weight). Poste detail rows render in the `muted` tone, with no
// emphasis, so the garantie main rows stand out (mahhp 22854 mockup).
//
// The `guarantee` column shows the garantie label (`LIB_GAR`) on a main row
// and the indented, muted poste label (`LIB_PSD`) on a detail row.
//
// Registered once at module load, mirroring `taskListConfig.tsx`.
// ---------------------------------------------------------------------------

const COST_CELL_RENDERER = 'claimflow-costCell';
const COST_CELL_RENDERER_EMPHASIS = 'claimflow-costCell-emphasis';
const COST_GUARANTEE_CELL_RENDERER = 'claimflow-costGuaranteeCell';

function renderCostCell(
  value: unknown,
  row: Record<string, unknown>,
  emphasis: boolean,
) {
  const poste = isPosteRow(row);
  return (
    <Text
      as="span"
      size="sm"
      color={poste ? 'muted' : 'secondary'}
      weight={emphasis && !poste ? 'medium' : 'normal'}
    >
      {formatCell(toCostValue(value))}
    </Text>
  );
}

dataListWorkflowRegistry.registerCellRenderer(COST_CELL_RENDERER, (value, row) =>
  renderCostCell(value, row, false),
);

dataListWorkflowRegistry.registerCellRenderer(
  COST_CELL_RENDERER_EMPHASIS,
  (value, row) => renderCostCell(value, row, true),
);

dataListWorkflowRegistry.registerCellRenderer(
  COST_GUARANTEE_CELL_RENDERER,
  (value, row) =>
    isPosteRow(row) ? (
      <Text as="span" size="sm" weight="normal" color="muted" className="pl-4">
        {safeString(row.poste)}
      </Text>
    ) : (
      safeString(value)
    ),
);

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * Header style parity: the original grid overrides the base Table header's
 * uppercase/tracked default with a small-caps, normal-tracking secondary tone.
 */
const HEADER_CLASSNAME =
  '!normal-case !text-xs !font-medium !tracking-normal text-secondary-500';

/** Stable key of the authoritative external-total column. */
const COST_COLUMN_KEY = 'cost';

/**
 * The columns whose Total-row figure is the **sum of the garanties' main
 * rows** (treating `null` as `0`) — poste detail rows never count, whatever
 * the toggle state. `cost` is excluded — its total is the authoritative
 * `totalCost` prop (see {@link totalForColumn}).
 */
const SUMMED_COLUMN_KEYS = [
  'provision',
  'settlement',
  'grossCharge',
  'forecast',
  'recourse',
] as const;

/**
 * Builds the declarative cost-synthesis `DataListConfig` for the already-mapped
 * rows to display (inline, client-only — no endpoint, no re-fetch). The stable
 * column keys are identical to the `CostLine` field names, so no
 * `fieldMapping` is needed: DataList reads `row[key]` directly.
 *
 * `totalCost` is also wired in via `columnTotalOverrides` (the `-dlto`
 * external-total API) so the authoritative page-level figure — not the sum of
 * the displayed `cost` column — is the source of truth for the `cost` footer.
 */
function createCostSynthesisConfig(
  rows: CostLine[],
  totalCost: number,
): DataListConfig {
  return {
    id: 'claimflow-cost-synthesis',

    dataSource: {
      // Inline, client-only data — DataList does not re-fetch.
      data: rows as unknown as Record<string, unknown>[],
    },

    // Authoritative cost total via the `-dlto` API: the `cost` footer figure
    // is the page-level `totalCost` prop, not the displayed-column sum.
    columnTotalOverrides: { [COST_COLUMN_KEY]: totalCost },

    columns: {
      guarantee: {
        label: 'Garantie',
        order: 1,
        align: 'left',
        type: 'custom',
        typeOptions: { renderer: COST_GUARANTEE_CELL_RENDERER },
        sortable: false,
        headerClassName: HEADER_CLASSNAME,
        className: 'text-sm font-medium',
      },
      provision: {
        label: 'Provision rest.',
        order: 2,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: COST_CELL_RENDERER },
        sortable: false,
        total: true,
        headerClassName: HEADER_CLASSNAME,
      },
      settlement: {
        label: 'Règlement',
        order: 3,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: COST_CELL_RENDERER },
        sortable: false,
        total: true,
        headerClassName: HEADER_CLASSNAME,
      },
      grossCharge: {
        label: 'Charge brute',
        order: 4,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: COST_CELL_RENDERER },
        sortable: false,
        total: true,
        headerClassName: HEADER_CLASSNAME,
      },
      forecast: {
        label: 'Prévision Rec',
        order: 5,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: COST_CELL_RENDERER },
        sortable: false,
        total: true,
        headerClassName: HEADER_CLASSNAME,
      },
      recourse: {
        label: 'Recours Enc',
        order: 6,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: COST_CELL_RENDERER },
        sortable: false,
        total: true,
        headerClassName: HEADER_CLASSNAME,
      },
      cost: {
        label: 'Coût Net',
        order: 7,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: COST_CELL_RENDERER_EMPHASIS },
        sortable: false,
        total: true,
        headerClassName: HEADER_CLASSNAME,
      },
    },

    // Poste detail rows sit on a faint tint under their garantie main row.
    formatting: {
      rowRules: [
        {
          condition: { field: 'isTotal', operator: 'equals', value: false },
          className: 'bg-secondary-50/50',
        },
      ],
    },

    display: {
      size: 'compact',
      hoverable: false,
    },

    sorting: {
      enabled: false,
    },

    toolbar: {
      visible: false,
    },

    // Shared fixed column layout so the host-rendered Total row (a separate
    // <table>) aligns 1:1 with these columns — see the Total-row <Table> below.
    cssOverrides: {
      table: 'table-fixed',
    },

    // Fixed, finite synthesis grid — no pagination footer.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },
  };
}

// ---------------------------------------------------------------------------
// Host-rendered Total row
//
// Why host-rendered (the spec's "Totals approach", option 3): the body cells
// must render a dash for `null`, which forces a `custom` cell renderer — and
// `custom` columns are excluded from DataList's native footer sums. So the
// footer is rendered here, reading the authoritative figures directly:
//   • provision/settlement/grossCharge/forecast/recourse = sum of the
//     garanties' main rows (null → 0) — never the poste detail rows, so the
//     figures are identical whether the "Postes" toggle is on or off.
//   • cost = the authoritative `totalCost` prop (the same value the
//     `columnTotalOverrides` entry carries), NOT the displayed-column sum.
// The footer iterates `list.visibleColumns`, so it tracks any column
// hide/reorder/label override exactly like the header and body.
// ---------------------------------------------------------------------------

/** Sum a numeric `CostLine` field across rows, treating `null` as `0`. */
function sumColumn(rows: CostLine[], key: (typeof SUMMED_COLUMN_KEYS)[number]): number {
  return rows.reduce((acc, line) => acc + (line[key] ?? 0), 0);
}

/**
 * Authoritative total displayed for a column key, or `null` when the column
 * carries no footer figure (e.g. the `guarantee` label column).
 */
function totalForColumn(
  key: string,
  guaranteeRows: CostLine[],
  totalCost: number,
): number | null {
  if (key === COST_COLUMN_KEY) return totalCost;
  if ((SUMMED_COLUMN_KEYS as readonly string[]).includes(key)) {
    return sumColumn(guaranteeRows, key as (typeof SUMMED_COLUMN_KEYS)[number]);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CostSynthesisCard({
  costs,
  totalCost,
  defaultShowPostes,
  _layoutOverrides,
}: Readonly<CostSynthesisCardProps>) {
  // The handler's own toggle choice; `null` until they flip it, so the toggle
  // follows the (tenant-overridable) `defaultShowPostes` prop until then.
  const [postesChoice, setPostesChoice] = useState<boolean | null>(null);
  const showPostes = postesChoice ?? defaultShowPostes ?? false;
  const postesSwitchId = useId();

  // The garanties' main rows — what the toggle-off view shows and what the
  // Total row sums, whatever the toggle state.
  const guaranteeRows = useMemo(
    () => costs.filter((line) => line.isTotal),
    [costs],
  );
  const displayedRows = showPostes ? costs : guaranteeRows;

  const baseConfig = useMemo(
    () => createCostSynthesisConfig(displayedRows, totalCost),
    [displayedRows, totalCost],
  );

  usePublishLayoutConfig(
    COMPONENT_ID,
    buildPublishedConfigFromDataList(baseConfig, COST_SYNTHESIS_CARD_TEXT.title),
  );

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const list = useDataList(config);

  // The first visible column carries the "Total" label (matching the original,
  // where "Total" sat in the left-most `guarantee` cell).
  const firstVisibleKey = list.visibleColumns[0]?.key;

  // `<DataList.Table/>` renders its built-in "no data" placeholder for zero
  // rows; the original grid showed no placeholder (just the Total row), so we
  // skip the data table when there is nothing to display and let the Total row
  // stand alone — preserving the original empty-state behaviour.
  const hasRows = displayedRows.length > 0;

  return (
    <Card padding="none">
      <Card.Header>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Icon icon={ClipboardList} size="md" color="primary" />
            <Text as="span" weight="semibold" size="base">
              {resolveCardTitle(COST_SYNTHESIS_CARD_TEXT.title, _layoutOverrides)}
            </Text>
          </div>
          <HStack gap={4} align="center">
            <HStack gap={2} align="center">
              <Text as="label" htmlFor={postesSwitchId} size="sm" className="cursor-pointer">
                {COST_SYNTHESIS_CARD_TEXT.postesToggle}
              </Text>
              <Switch
                id={postesSwitchId}
                checked={showPostes}
                onChange={(event) => {
                  setPostesChoice(event.target.checked);
                }}
              />
            </HStack>
            <Text as="span" weight="bold" size="base" className="text-green-600">
              {formatCurrencyFr(totalCost)}
            </Text>
          </HStack>
        </div>
      </Card.Header>
      <Card.Content>
        <DataList config={config} list={list}>
          {hasRows && <DataList.Table />}
          {/* Host-rendered Total row (always shown, including empty costs).
              `table-fixed` mirrors the DataList table's layout so this row's
              columns line up 1:1 with the body columns above. */}
          <Table size="compact" className="table-fixed">
            <Table.Body>
              <Table.Row hoverable={false}>
                {list.visibleColumns.map((column) => {
                  const total = totalForColumn(column.key, guaranteeRows, totalCost);
                  if (column.key === firstVisibleKey && total === null) {
                    return (
                      <Table.Cell key={column.key} align={column.align}>
                        <Text as="span" size="sm" weight="bold">
                          {COST_SYNTHESIS_CARD_TEXT.totalRow}
                        </Text>
                      </Table.Cell>
                    );
                  }
                  return (
                    <Table.Cell key={column.key} align={column.align ?? 'right'}>
                      {total === null ? null : (
                        <span
                          className={
                            column.key === COST_COLUMN_KEY
                              ? 'text-sm font-bold text-green-600'
                              : 'text-sm font-semibold text-secondary-800'
                          }
                        >
                          {formatCurrencyFr(total)}
                        </span>
                      )}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            </Table.Body>
          </Table>
        </DataList>
      </Card.Content>
    </Card>
  );
}
